import { useMonitoringStore } from '@/stores/monitoring.store'
import type {
  SignalingState,
  IceDetailState,
  MediaFlowState,
  MediaServerState,
  DCPayloadMediaStateChanged,
  DCPayloadMediaQualityReport,
} from '@dronecast/shared'

/**
 * 모니터링 서비스
 *
 * 네트워크 온/오프라인 감지, WebSocket/WebRTC 상태 추적을 담당하고
 * monitoring store를 업데이트한다.
 */
class MonitoringService {
  private started = false

  start() {
    if (this.started) return
    this.started = true

    const store = useMonitoringStore()

    const updateOnline = () => store.updateNetwork(navigator.onLine)
    window.addEventListener('online', updateOnline)
    window.addEventListener('offline', updateOnline)
    updateOnline()
  }

  /**
   * WebSocket 상태 변경 시 호출
   */
  updateSignalingState(role: 'caster' | 'player', wsState: 'connecting' | 'connected' | 'reconnecting' | 'disconnected', reconnectAttempts = 0) {
    const store = useMonitoringStore()
    const stateMap: Record<string, SignalingState> = {
      connecting: 'connecting',
      connected: 'connected',
      reconnecting: 'reconnecting',
      disconnected: 'disconnected',
    }
    const next = stateMap[wsState] || 'disconnected'
    store.updateSignaling(role, next, reconnectAttempts)
    store.pushEvent({ role, segment: 'signaling', state: next })
  }

  /**
   * WebRTC 상태 변경 시 호출 (connectionState 또는 iceConnectionState)
   */
  updateWebRtcState(role: 'caster' | 'player', connectionState?: string, iceState?: string) {
    const store = useMonitoringStore()

    if (iceState) {
      const iceMap: Record<string, IceDetailState> = {
        new: 'gathering',
        checking: 'checking',
        connected: 'connected',
        completed: 'connected',
        disconnected: 'disconnected',
        failed: 'failed',
        closed: 'idle',
      }
      const next = iceMap[iceState] || 'idle'
      store.updateIce(role, next)
      store.pushEvent({ role, segment: 'ice', state: next, message: `client: ${iceState}` })
    }

    if (connectionState) {
      const flowMap: Record<string, MediaFlowState> = {
        connected: 'flowing',
        disconnected: 'not_flowing',
        failed: 'not_flowing',
        closed: 'idle',
      }
      if (flowMap[connectionState]) {
        store.updateMediaFlow(role, flowMap[connectionState])
        store.pushEvent({ role, segment: 'mediaFlow', state: flowMap[connectionState], message: `client: ${connectionState}` })
      }
    }
  }

  /**
   * SDP 교환 시작
   */
  updateSdpStarted(role: 'caster' | 'player') {
    const store = useMonitoringStore()
    store.updateSdp(role, 'in_progress')
  }

  /**
   * SDP 교환 완료/실패
   */
  updateSdpResult(role: 'caster' | 'player', success: boolean, elapsedMs?: number) {
    const store = useMonitoringStore()
    store.updateSdp(role, success ? 'completed' : 'failed', elapsedMs)
  }

  /**
   * 미디어 서버 상태 업데이트 (서버에서 evt.server.health 수신 시)
   */
  updateServerHealth(connected: boolean) {
    const store = useMonitoringStore()
    const state: MediaServerState = connected ? 'healthy' : 'error'
    store.updateMediaServer(state)
  }

  /**
   * 캐스팅 상태 업데이트 (ON AIR + Player 수)
   */
  updateCastingStatus(isOnAir: boolean, playerCount: number) {
    const store = useMonitoringStore()
    store.updateCasting(isOnAir, playerCount)
  }

  /**
   * 서버에서 push한 evt.media.state_changed 처리 (BL-604).
   * 미디어 서버 측에서 본 endpoint의 ICE/미디어 흐름 상태를 monitoring store에 반영.
   */
  handleServerMediaStateChanged(payload: DCPayloadMediaStateChanged) {
    const store = useMonitoringStore()
    const role = payload.targetRole === 'caster' ? 'caster' : 'player'
    const evt = payload.event
    const newState = (payload.newState || '').toUpperCase()

    if (evt === 'ice_component_state_changed') {
      const iceMap: Record<string, IceDetailState> = {
        GATHERING: 'gathering',
        CHECKING: 'checking',
        CONNECTED: 'connected',
        READY: 'connected',
        FAILED: 'failed',
        DISCONNECTED: 'disconnected',
      }
      const next = iceMap[newState] || 'idle'
      store.updateIce(role, next)
      store.pushEvent({ role, segment: 'ice', state: next, message: `server: ${payload.event}=${payload.newState}` })
    } else if (evt === 'media_flow_in_state_changed' || evt === 'media_flow_out_state_changed') {
      const next: 'flowing' | 'not_flowing' | null =
        newState === 'FLOWING' ? 'flowing' : newState === 'NOT_FLOWING' ? 'not_flowing' : null
      if (next) {
        store.updateMediaFlow(role, next)
        store.pushEvent({ role, segment: 'mediaFlow', state: next, message: `server: ${payload.event}=${payload.newState}` })
      }
    } else if (evt === 'connection_state_changed') {
      const next: 'flowing' | 'not_flowing' | null =
        newState === 'CONNECTED' ? 'flowing' : newState === 'DISCONNECTED' ? 'not_flowing' : null
      if (next) {
        store.updateMediaFlow(role, next)
        store.pushEvent({ role, segment: 'mediaFlow', state: next, message: `server: connection=${payload.newState}` })
      }
    }
  }

  /**
   * 서버에서 push한 evt.media.quality_report 처리 (BL-604).
   * EndpointStats의 핵심 지표를 monitoring store mediaFlow에 반영.
   */
  handleServerMediaQualityReport(payload: DCPayloadMediaQualityReport) {
    const store = useMonitoringStore()
    const role = payload.targetRole === 'caster' ? 'caster' : 'player'
    const stats = (payload.stats || {}) as any
    store.updateMediaFlow(role, 'flowing', {
      bitrate: stats.inboundBitrateKbps,
      packetLoss: stats.inboundPacketLossRate,
      frameRate: stats.inboundFps,
      jitter: stats.jitterMs,
    })
  }

  /**
   * 리셋
   */
  resetRole(role: 'caster' | 'player') {
    const store = useMonitoringStore()
    if (role === 'caster') {
      store.updateSignaling('caster', 'disconnected')
      store.updateSdp('caster', 'idle')
      store.updateIce('caster', 'idle')
      store.updateMediaFlow('caster', 'idle')
      store.updateCasting(false, 0)
      store.updatePlayers([])
    } else {
      store.updateSignaling('player', 'disconnected')
      store.updateSdp('player', 'idle')
      store.updateIce('player', 'idle')
      store.updateMediaFlow('player', 'idle')
      store.updateCasterServerLink({ status: 'unknown', updatedAt: Date.now() })
    }
  }
}

export const monitoringService = new MonitoringService()
